import { createFileRoute, useRouter } from "@tanstack/react-router";
import { FormProvider } from "react-hook-form";
import {
  isOutOfIterations,
  useBillingStatus,
} from "../components/BillingStatusContext";
import { Header } from "../components/Header";
import { LoadingSpinner } from "../components/LoadingSpinner";
import { OutOfIterationsOverlay } from "../components/OutOfIterations";
import { RefineBottomBar } from "../components/refine/RefineBottomBar";
import { RefineDrawer } from "../components/refine/RefineDrawer";
import { RefineStage } from "../components/refine/RefineStage";
import { RefineTopBar } from "../components/refine/RefineTopBar";
import { type LoadedRefinePage, useRefinePage } from "../hooks/useRefinePage";
import { getErrorMessage } from "../lib/api-client";
import { redirectHomeOnInvalidSearch } from "../lib/route-guards";
import { refineSearchSchema } from "../lib/route-schemas";

export const Route = createFileRoute("/refine")({
  validateSearch: refineSearchSchema,
  onError: redirectHomeOnInvalidSearch,
  component: RefinePage,
});

function RefinePageContent({ page }: { page: LoadedRefinePage }) {
  const { billingStatus, isBillingLoading } = useBillingStatus();
  const outOfIterations = isOutOfIterations(billingStatus, isBillingLoading);

  return (
    <FormProvider {...page.formMethods}>
      <main className="flex min-h-0 flex-1 px-6 pb-6 pt-2">
        <div className="relative flex min-h-0 flex-1 overflow-hidden rounded-xl">
          {/* While the overlay is up the whole workspace is inert: the overlay
              swallows mouse clicks, and inert + aria-hidden keeps keyboard and
              screen-reader users off the controls behind it. The outer Header
              stays reachable. */}
          <div
            inert={outOfIterations}
            aria-hidden={outOfIterations}
            className="flex min-h-0 min-w-0 flex-1"
          >
            <RefineDrawer page={page} />
            <div className="flex min-h-0 min-w-0 flex-1 flex-col bg-[var(--bg-canvas)]">
              <div className="flex shrink-0 items-center p-6">
                <RefineTopBar image={page.image} />
              </div>
              <div className="flex min-h-0 flex-1 items-center justify-center px-6 [container-type:size]">
                <RefineStage
                  image={page.image}
                  isRegenerating={page.isRegenerating}
                  selection={page.selection}
                />
              </div>
              <div className="flex shrink-0 flex-col p-6">
                <RefineBottomBar page={page} />
              </div>
            </div>
          </div>
          {outOfIterations && <OutOfIterationsOverlay />}
        </div>
      </main>
    </FormProvider>
  );
}

function RefineLoadingScreen() {
  return (
    <div className="min-h-screen bg-[var(--bg-default)] flex items-center justify-center">
      <LoadingSpinner text="Loading image..." size="lg" />
    </div>
  );
}

function RefineErrorScreen({
  error,
  onBack,
}: {
  error: unknown;
  onBack: () => void;
}) {
  return (
    <div className="min-h-screen bg-[var(--bg-default)] flex flex-col items-center justify-center p-8">
      <div className="text-[var(--accent-destructive)] mb-4 text-xl">
        {error ? getErrorMessage(error) : "Image not found"}
      </div>
      <button
        type="button"
        onClick={onBack}
        className="text-[var(--accent-secondary-hover)] hover:text-[var(--accent-secondary-hover-stroke)]"
      >
        Back to Results
      </button>
    </div>
  );
}

function RefinePage() {
  const { imageId } = Route.useSearch();
  const router = useRouter();
  const page = useRefinePage(imageId);

  if (page.isLoading) return <RefineLoadingScreen />;
  if (page.error || !page.image)
    return (
      <RefineErrorScreen
        error={page.error}
        onBack={() => router.history.back()}
      />
    );

  return (
    <div className="h-dvh bg-[var(--bg-default)] flex flex-col">
      <Header currentPage="refine" />
      <RefinePageContent page={page as LoadedRefinePage} />
    </div>
  );
}
